// See: backend/docs/driver-scheduler.md §11 — POST /optimize-day
//
// Sprint 3 — Toplu re-optimization modal'ı.
//
// Akış:
//   1. Modal açılınca otomatik dryRun=true ile preview çek
//   2. Operatöre before/after stats + değişiklik listesi göster
//   3. "Uygula" butonu → aynı endpoint dryRun=false ile çağır
//   4. Başarıyla uygulanırsa onApplied() callback ile parent'a haber ver

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "shared/components/UIElements/Modal";
import Button from "shared/components/FormElements/Button";

import useDriverSchedulerApi from "./hooks/useDriverSchedulerApi";
import OptimizeDayChangeRow from "./components/OptimizeDayChangeRow";

import "./OptimizeDayModal.css";

const OptimizeDayModal = ({ show, onClose, date, onApplied }) => {
  const { t } = useTranslation();
  const { isLoading, optimizeDay } = useDriverSchedulerApi();

  const [preview, setPreview] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null); // post-apply summary

  const runDryRun = useCallback(async () => {
    if (!date) return;
    setErrorMessage(null);
    setApplyResult(null);
    setPreview(null);
    try {
      const result = await optimizeDay(date, { dryRun: true });
      setPreview(result);
    } catch (err) {
      setErrorMessage(err?.message || t("driverScheduler.optimize.fetchError"));
    }
  }, [date, optimizeDay, t]);

  useEffect(() => {
    if (show && date) {
      runDryRun();
    }
    if (!show) {
      setPreview(null);
      setErrorMessage(null);
      setApplyResult(null);
      setIsApplying(false);
    }
  }, [show, date, runDryRun]);

  const handleApply = useCallback(async () => {
    if (!date || isApplying) return;
    setIsApplying(true);
    setErrorMessage(null);
    try {
      const result = await optimizeDay(date, { dryRun: false });
      setApplyResult(result);
      window?.toast?.success?.(t("driverScheduler.optimize.appliedToast", { count: result?.appliedCount || 0 }));
      if (typeof onApplied === "function") onApplied(result);
    } catch (err) {
      setErrorMessage(err?.message || t("driverScheduler.optimize.applyError"));
    } finally {
      setIsApplying(false);
    }
  }, [date, isApplying, optimizeDay, onApplied, t]);

  const displayed = applyResult || preview;
  const hasChanges = (displayed?.changes || []).length > 0;
  const canApply = !isApplying && !applyResult && preview && hasChanges;

  return (
    <Modal
      show={show}
      onCancel={onClose}
      header={`${t("driverScheduler.optimize.title")} — ${date}`}
      contentClass="optimize-modal__content"
      footerClass="optimize-modal__footer"
      footer={
        <>
          <Button inverse onClick={onClose} disabled={isApplying}>
            {applyResult ? t("driverScheduler.optimize.close") : t("driverScheduler.optimize.cancel")}
          </Button>
          {!applyResult && (
            <Button onClick={handleApply} disabled={!canApply}>
              {isApplying
                ? t("driverScheduler.optimize.applying")
                : t("driverScheduler.optimize.apply", { count: displayed?.changes?.length || 0 })}
            </Button>
          )}
        </>
      }
    >
      {/* Loading */}
      {isLoading && !displayed && (
        <div className="optimize-modal__loading">{t("driverScheduler.optimize.loading")}</div>
      )}

      {/* Error */}
      {errorMessage && <div className="optimize-modal__error">{errorMessage}</div>}

      {/* No data yet */}
      {!isLoading && !displayed && !errorMessage && (
        <div className="optimize-modal__empty">{t("driverScheduler.optimize.noData")}</div>
      )}

      {/* Preview / Result */}
      {displayed && (
        <>
          {displayed.message && <div className="optimize-modal__info">{displayed.message}</div>}

          {applyResult && (
            <div className="optimize-modal__apply-banner">
              {t("driverScheduler.optimize.appliedBanner", {
                count: applyResult.appliedCount || 0,
                total: applyResult.changes?.length || 0,
              })}
            </div>
          )}

          {/* Stats karşılaştırma */}
          <StatsComparison before={displayed.before} after={displayed.after} t={t} />

          {/* Skipped / infeasible özet */}
          <div className="optimize-modal__meta">
            <span>
              <strong>{displayed.lockedSkipped || 0}</strong> {t("driverScheduler.optimize.lockedSkipped")}
            </span>
            <span>
              <strong>{(displayed.infeasibleAfter || []).length}</strong>{" "}
              {t("driverScheduler.optimize.infeasibleAfter")}
            </span>
            <span>
              <strong>{(displayed.invalidTrips || []).length}</strong>{" "}
              {t("driverScheduler.optimize.invalidTrips")}
            </span>
          </div>

          {/* Değişiklikler listesi */}
          {hasChanges ? (
            <div className="optimize-modal__changes">
              <h4>
                {t("driverScheduler.optimize.proposedChanges", { count: displayed.changes.length })}
              </h4>
              <div className="optimize-modal__changes-list">
                {displayed.changes.map((change) => (
                  <OptimizeDayChangeRow key={change.serviceId} change={change} />
                ))}
              </div>
            </div>
          ) : (
            <div className="optimize-modal__no-changes">
              {t("driverScheduler.optimize.noChanges")}
            </div>
          )}

          {/* Infeasible detayları */}
          {(displayed.infeasibleAfter || []).length > 0 && (
            <div className="optimize-modal__infeasible">
              <h4>{t("driverScheduler.optimize.infeasibleHeader")}</h4>
              <ul>
                {displayed.infeasibleAfter.map((item) => (
                  <li key={item.serviceId}>
                    <strong>{item.serviceName || item.serviceId}</strong>
                    {item.reason ? ` — ${t(`driverScheduler.infeasibleReason.${item.reason}`)}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Apply hataları */}
          {applyResult?.appliedErrors?.length > 0 && (
            <div className="optimize-modal__infeasible" style={{ marginTop: 12 }}>
              <h4 style={{ color: "#c62828" }}>{t("driverScheduler.optimize.applyErrorsHeader")}</h4>
              <ul>
                {applyResult.appliedErrors.map((e, idx) => (
                  <li key={idx}>
                    <strong>{e.serviceId}</strong>: {e.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

const StatsComparison = ({ before, after, t }) => {
  if (!before || !after) return null;
  const rows = [
    { key: "totalCost", label: t("driverScheduler.optimize.stats.totalCost") },
    { key: "totalDeadheadKm", label: t("driverScheduler.optimize.stats.totalDeadheadKm"), unit: "km" },
    { key: "totalDeadheadMin", label: t("driverScheduler.optimize.stats.totalDeadheadMin"), unit: "dk" },
    { key: "totalIdleMin", label: t("driverScheduler.optimize.stats.totalIdleMin"), unit: "dk" },
    { key: "activeDriverCount", label: t("driverScheduler.optimize.stats.activeDriverCount") },
  ];

  return (
    <table className="optimize-modal__stats-table">
      <thead>
        <tr>
          <th />
          <th>{t("driverScheduler.optimize.before")}</th>
          <th>{t("driverScheduler.optimize.after")}</th>
          <th>{t("driverScheduler.optimize.diff")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const b = before[row.key] ?? 0;
          const a = after[row.key] ?? 0;
          const delta = typeof a === "number" && typeof b === "number" ? a - b : null;
          const better =
            row.key === "activeDriverCount"
              ? delta > 0 // daha çok aktif sürücü iyi (yük dağılımı)
              : delta < 0; // diğerleri için azalma iyi
          return (
            <tr key={row.key}>
              <td>{row.label}</td>
              <td>{formatStat(b, row.unit)}</td>
              <td>{formatStat(a, row.unit)}</td>
              <td className={delta === 0 ? "" : better ? "is-better" : "is-worse"}>
                {delta !== null
                  ? `${delta > 0 ? "+" : ""}${typeof delta === "number" ? delta.toFixed(2) : delta}${row.unit ? " " + row.unit : ""}`
                  : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

function formatStat(v, unit) {
  if (typeof v !== "number") return "—";
  return `${v.toFixed ? v.toFixed(2) : v}${unit ? " " + unit : ""}`;
}

export default OptimizeDayModal;
